import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, ViewEncapsulation, inject } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { lastValueFrom } from 'rxjs';

import { Client } from 'src/providers/client/client';
import { ToDos } from 'src/providers/to-dos/to-dos';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';

import { ConciergeApiService } from 'src/shared/services/concierge-api.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';

import { AttachmentModel } from 'src/models/concierge.model';
import { CHECKLIST_TEMPLATES, ChecklistTemplate, TemplateItem } from 'src/shared/constants/checklist-templates';
import { RadioButtonModel } from 'src/tidy-ui-components/components/form/radio-button/radio-button.model';

@Component({
  selector: 'checklists-onboarding',
  templateUrl: './checklists-onboarding.component.html',
  styleUrls: ['./checklists-onboarding.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class ChecklistsOnboardingComponent implements OnInit, OnDestroy {
  private client = inject(Client);
  private rightSidePanelService = inject(RightSidePanelService);
  private navCtrl = inject(CustomNavController);
  private cdr = inject(ChangeDetectorRef);
  private util = inject(Util);
  private storage = inject(TidyStorage);
  private conciergeApi = inject(ConciergeApiService);
  private fb = inject(FormBuilder);
  private toDos = inject(ToDos);
  private onboardingProvider = inject(OnboardingProvider);
  windowService = inject(WindowService);

  private metadataKey: string;
  private addressId: number;
  private readonly stepId = 'checklists';

  loaded = false;
  fromGettingStarted = false;
  isCreating = false;
  attachments: AttachmentModel[] = [];
  submitted = false;
  currentStep: 1 | 2 | 3 = 1;

  templates: RadioButtonModel[] = CHECKLIST_TEMPLATES.map(t => ({
    value: t.id,
    viewValue: t.name,
    extraText: t.description
  }));

  itemForm = this.fb.group({
    method: [''],
    templateId: [''],
    body: ['']
  });

  get progressPercent(): number {
    return Math.round((this.currentStep / 3) * 100);
  }

  async ngOnInit() {
    const params = await this.storage.retrieve('dialog-params');
    this.addressId = params?.addressId;
    this.fromGettingStarted = !!params?.fromGettingStarted;
    this.metadataKey = `onboarding_step.${this.addressId}.${this.stepId}`;
    this.rightSidePanelService.setDialogPageTitle('Checklists');
    this.loaded = true;
    this.cdr.markForCheck();
  }

  private async markStepCompleted() {
    await this.client.addMetadata({
      key: this.metadataKey,
      value: 'completed'
    });
  }

  private navigateBack() {
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.navCtrl.navigateForward('getting-started');
    }
  }

  skip = async () => {
    try {
      await this.markStepCompleted();
      this.navigateBack();
    } catch (err) {
      this.util.showError('Failed to complete step. Please try again.');
    }
  };

  ngOnDestroy() {
    this.rightSidePanelService.customBackAction$.next(null);
  }

  private updateBackAction() {
    if (this.currentStep > 1) {
      this.rightSidePanelService.customBackAction$.next(() => {
        this.currentStep = (this.currentStep - 1) as 1 | 2 | 3;
        this.cdr.markForCheck();
        this.updateBackAction();
      });
    } else {
      this.rightSidePanelService.customBackAction$.next(null);
    }
  }

  selectMethod = (method: 'yes' | 'no') => {
    this.itemForm.controls.method.setValue(method);
    this.currentStep = 2;
    this.updateBackAction();
    this.cdr.markForCheck();
  };

  selectTemplate = (templateId: string) => {
    this.itemForm.controls.templateId.setValue(templateId);
    this.currentStep = 3;
    this.updateBackAction();
    this.cdr.markForCheck();
  };

  goToNextStep = async () => {
    if (this.currentStep === 2 && this.itemForm.value.method === 'yes' && !this.attachments.length) {
      this.util.showError('Please upload a checklist.', 5000);
      return;
    }
    this.currentStep = (this.currentStep + 1) as 1 | 2 | 3;
    this.updateBackAction();
    this.cdr.markForCheck();
  };

  finish = async () => {
    const method = this.itemForm.value.method;
    if (method === 'yes') {
      await this.saveUpload();
    } else if (method === 'no') {
      await this.createFromTemplate();
    }
  };

  addAttachment = async () => {
    try {
      const attachment = await this.conciergeApi.addAttachment();
      this.attachments.push(attachment);
      this.cdr.markForCheck();
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  };

  removeAttachment = (index: number) => {
    this.attachments.splice(index, 1);
    this.cdr.markForCheck();
  };

  private async saveUpload() {
    const data = {
      body: this.itemForm.value.body || '',
      subject: 'Please add my checklists',
      attachments: this.attachments.map(item => item.fileKey),
      emails: [] as string[]
    };
    const item = {
      data,
      type: 'support.concierge_task',
    };
    try {
      await Promise.all([
        lastValueFrom(this.conciergeApi.createConciergeItem(item)),
        this.markStepCompleted()
      ]);
      this.navigateBack();
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  private async createFromTemplate() {
    const templateId = this.itemForm.value.templateId;
    if (!templateId) {
      this.util.showWarning('Please select a template.', 5000);
      return;
    }

    const template = CHECKLIST_TEMPLATES.find(t => t.id === templateId);
    if (!template) {
      return;
    }

    if (this.isCreating) {
      return;
    }
    this.isCreating = true;
    this.cdr.markForCheck();

    try {
      const addressId = this.addressId || parseInt(localStorage.getItem('addressId') || '0', 10);
      const existingLists = await this.toDos.getAllToDoListsForAddress(addressId);
      const uniqueTitle = this.getUniqueListTitle(template.name, existingLists);

      const listResponse = await this.toDos.createList({
        all_addresses: true,
        title: uniqueTitle,
        before_after_photos_state: 'inactive',
        description: template.description,
      });
      const taskListId = listResponse.id;
      localStorage.setItem('taskListId', taskListId);

      await this.processWithConcurrency(template.sections, async (section) => {
        const groupResponse = await this.toDos.addGroup({ name: section.name });
        const groupId = groupResponse.id;
        await this.toDos.updateGroup(groupId, { address_task_list_id: taskListId });

        await this.processWithConcurrency(section.items, async (item: TemplateItem) => {
          await this.toDos.addToDo(addressId, taskListId, {
            title: item.title,
            note: item.note || '',
            estimated_time: 0,
            is_important: item.is_important || false,
            task_category_options: [],
            address_task_group_ids: [groupId],
            address_id: addressId,
            address_task_list_id: taskListId,
            task_type: 'cleaning',
          });
        }, 2);
      }, 2);

      const noteBody = this.itemForm.value.body?.trim();
      if (noteBody) {
        const data = {
          body: noteBody,
          subject: 'Please add my checklists',
          attachments: [] as string[],
          emails: [] as string[]
        };
        await lastValueFrom(this.conciergeApi.createConciergeItem({ data, type: 'support.concierge_task' }));
      }

      await this.markStepCompleted();
      await this.onboardingProvider.markGoalAsCompleted('toDos');
      this.onboardingProvider.setShowOnboardingOnPage(false);
      this.onboardingProvider.setShowOnboardingInHeader(true);
      this.navigateBack();
    } catch (err) {
      this.util.showError((err as any)?.error?.message || (err as Error).message, 10000);
    } finally {
      this.isCreating = false;
      this.cdr.markForCheck();
    }
  }

  private async processWithConcurrency<T>(
    items: T[],
    processor: (item: T) => Promise<void>,
    maxConcurrent: number
  ): Promise<void> {
    for (let i = 0; i < items.length; i += maxConcurrent) {
      const batch = items.slice(i, i + maxConcurrent);
      await Promise.all(batch.map(processor));
    }
  }

  private getUniqueListTitle(baseTitle: string, existingLists: any[]): string {
    const existingTitles = existingLists.map(list => list.title.toLowerCase());
    if (!existingTitles.includes(baseTitle.toLowerCase())) {
      return baseTitle;
    }
    let counter = 2;
    while (existingTitles.includes(`${baseTitle} ${counter}`.toLowerCase())) {
      counter++;
    }
    return `${baseTitle} ${counter}`;
  }
}
